import "./LeaveCalendar.scss";
import "react-datepicker/dist/react-datepicker.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchData, getYears, Notify } from "../utils/constants";
import { Grid, Select, Spin, Typography, Row, Col } from "antd";
import StatisticsWidget from "../components/Statistics/Statistics";
import AnnualLeaveTable from "../components/Tables/AnnualLeaveTable";
import { AnnualPlanItem, LeaveRequest, BankHolidays, Stock } from "../utils/types";
import { useUser } from "../context/UserContext";
import RequestsTeamModal from "../components/Modals/RequestsTeamModal";
import AnnualLeaveTableMobile from "../components/Tables/AnnualLeaveTableMobile";
import TeamLeaveRequests from "../components/Sections/TeamLeaveRequests";
import StockRecord from "../components/Sections/StockRecord";
import { getLeaveLegendColors } from "../utils/generalConfigurationService";
import {
  DEFAULT_EMPLOYEE_STATUS_FILTER,
  EMPLOYEE_STATUS_OPTIONS,
  EmployeeStatusFilterValue,
  matchesEmployeeStatus,
} from "../components/Filters/EmployeeStatusFilter";

// PENDING/APPROVED are status buckets, not real request types - a request in
// any "still pending" status (AWAITING_MANAGER, AWAITING_ADMINISTRATIV,
// RETURNED_FOR_CORRECTION) is shown/hidden by the PENDING toggle, and the
// finalized "APPROVED" status by the APPROVED toggle.
const PENDING_STATUSES = [
  "AWAITING_MANAGER",
  "AWAITING_ADMINISTRATIV",
  "RETURNED_FOR_CORRECTION",
  "AWAITING_DOCUMENT_REVIEW",
];
const FINAL_APPROVED_STATUSES = ["APPROVED"];

const LEGEND_TYPES = [
  "ANNUAL_LEAVE",
  "MARRIAGE",
  "CHILD_BIRTH",
  "DEATH_OF_RELATIVE",
  "BLOOD_DONATION",
  "UNPAID_LEAVE",
];
const PLAN_LEGEND_TYPE = "ANNUAL_PLAN";
const ALL_LEGEND_TYPES = [
  "PENDING",
  "APPROVED",
  "BANK_HOLIDAY",
  ...LEGEND_TYPES,
  PLAN_LEGEND_TYPE,
];

const years = getYears();
const monthOptions = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
].map((label, value) => ({ label, value }));

const TeamCalendar = () => {
  const { authUser, roles } = useUser();
  const [users, setUsers] = useState<Stock[]>([]);
  const [bankHolidays, setBankHolidays] = useState<BankHolidays[]>([]);
  const [annualLeaveYear, setAnnualLeaveYear] = useState(years[1]);
  // Empty = no explicit filter, so every type shows (see selectedLeaveTypes
  // below) - clicking a legend button switches to "show only what's
  // clicked", clicking another adds it, and clicking an already-active one
  // removes it; clearing the last one reverts to showing everything again.
  // Bank Holiday sits outside this filter-mode logic entirely (see
  // bankHolidayVisible below) - it always shows regardless of what else is
  // selected, until it's explicitly clicked off.
  const [activeLeaveTypeFilters, setActiveLeaveTypeFilters] = useState<
    string[]
  >([]);
  const [bankHolidayVisible, setBankHolidayVisible] = useState<boolean>(true);
  const selectedLeaveTypes = useMemo(() => {
    const otherTypes = ALL_LEGEND_TYPES.filter((t) => t !== "BANK_HOLIDAY");
    const activeOtherFilters = activeLeaveTypeFilters.filter(
      (t) => t !== "BANK_HOLIDAY",
    );
    const base =
      activeOtherFilters.length > 0 ? activeOtherFilters : otherTypes;
    return bankHolidayVisible ? [...base, "BANK_HOLIDAY"] : base;
  }, [activeLeaveTypeFilters, bankHolidayVisible]);
  // Empty = show every employee (current behavior); once the user picks
  // specific names, only those rows show on the calendar.
  const [selectedEmployeeNames, setSelectedEmployeeNames] = useState<
    string[]
  >([]);
  const [employeeStatusFilter, setEmployeeStatusFilter] =
    useState<EmployeeStatusFilterValue>(DEFAULT_EMPLOYEE_STATUS_FILTER);
  const [legendColors, setLegendColors] = useState<Record<string, string>>({});
  const [annualPlan, setAnnualPlan] = useState<AnnualPlanItem[]>([]);

  const [isStatisticsModalOpen, setIsStatisticsModalOpen] =
    useState<boolean>(false);
  const [statisticModalType, setStatisticModalType] = useState<
    "Approved" | "Rejected" | "Cancelled" | "Pending" | "PendingCancelled"
  >("Approved");
  const [loading, setLoading] = useState<boolean>(false);
  const [allLeaveReq, setAllLeaveReq] = useState<LeaveRequest[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [focusedMonth, setFocusedMonth] = useState<number>(new Date().getMonth());
  const normalizedRoles = useMemo(
    () => roles.map((role) => role.replace(/^ROLE_/, "")),
    [roles],
  );
  const isAdmin = normalizedRoles.includes("ADMIN");
  const isHR = normalizedRoles.includes("HR");
  const isManager = normalizedRoles.includes("MANAGER");

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      await fetchData(
        `${process.env.REACT_APP_API_URL}/leave-balance/getLeaveBalancesForTeam/${annualLeaveYear.value}`,
        setUsers,
      );
    } catch (error) {
      console.error("Error while fetching users:", error);
      Notify("error", "Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  }, [annualLeaveYear]);

  const fetchHolidays = useCallback(async () => {
    try {
      setLoading(true);
      await fetchData(
        `${process.env.REACT_APP_API_URL}/holidays/getAllHolidays`,
        setBankHolidays,
      );
    } catch (error) {
      console.error("Error while fetching holidays:", error);
      Notify("error", "Failed to fetch holidays.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLegendColors = useCallback(async () => {
    try {
      const config = await getLeaveLegendColors();
      setLegendColors(config.colors || {});
    } catch (error) {
      console.error("Error while fetching legend colors:", error);
    }
  }, []);

  const fetchTeamRequests = useCallback(async () => {
    const requestsEndpoint =
      isAdmin || isHR
        ? `${process.env.REACT_APP_API_URL}/getAllLeaveRequests`
        : isManager
          ? `${process.env.REACT_APP_API_URL}/getRequestsByTeam`
          : null;

    if (!requestsEndpoint) {
      setAllLeaveReq([]);
      setIsDataLoaded(true);
      return;
    }

    try {
      setLoading(true);
      await fetchData(requestsEndpoint, setAllLeaveReq);
      setIsDataLoaded(true);
    } catch (error) {
      console.error("Error while fetching user requests:", error);
      Notify("error", "Failed to fetch user requests.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isHR, isManager]);

  const fetchAnnualPlan = useCallback(async () => {
    const planEndpoint =
      isAdmin || isHR
        ? `${process.env.REACT_APP_API_URL}/leave-planning/getAllLeaveSchedules`
        : isManager
          ? `${process.env.REACT_APP_API_URL}/leave-planning/getAllLeaveSchedulesByTeam`
          : null;

    if (!planEndpoint) {
      setAnnualPlan([]);
      return;
    }

    try {
      await fetchData(planEndpoint, setAnnualPlan);
    } catch (error) {
      console.error("Error while fetching team annual plan:", error);
    }
  }, [isAdmin, isHR, isManager]);

  const filteredLeaveRequests = useMemo(() => {
    return allLeaveReq.filter(
      (req) =>
        PENDING_STATUSES.includes(req.requestStatus) ||
        FINAL_APPROVED_STATUSES.includes(req.requestStatus),
    );
  }, [allLeaveReq]);

  // The Active filter (Yes/No/All) narrows the row set first; the
  // Employees picker then works within it.
  const statusUsers = useMemo(
    () =>
      users.filter((u) =>
        matchesEmployeeStatus(u.employeeStatus, employeeStatusFilter),
      ),
    [users, employeeStatusFilter],
  );
  const statusEmployeeNames = useMemo(
    () => new Set(statusUsers.map((u) => u.employeeName)),
    [statusUsers],
  );

  const employeeOptions = useMemo(
    () =>
      Array.from(statusEmployeeNames)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ label: name, value: name })),
    [statusEmployeeNames],
  );

  // Drop picked names that the status filter has just hidden.
  useEffect(() => {
    setSelectedEmployeeNames((prev) => {
      const next = prev.filter((name) => statusEmployeeNames.has(name));
      return next.length === prev.length ? prev : next;
    });
  }, [statusEmployeeNames]);

  // Filtered together so a request/plan entry for a hidden employee never
  // sneaks a row back in - AnnualLeaveTable builds its row set from both
  // `users` and `leaveRequests` (see its groupedArray), not just `users`.
  const visibleUsers = useMemo(
    () =>
      selectedEmployeeNames.length === 0
        ? statusUsers
        : statusUsers.filter((u) =>
            selectedEmployeeNames.includes(u.employeeName),
          ),
    [statusUsers, selectedEmployeeNames],
  );
  const visibleLeaveRequests = useMemo(() => {
    const statusFiltered = filteredLeaveRequests.filter((r) =>
      matchesEmployeeStatus(r.employeeStatus, employeeStatusFilter),
    );
    return selectedEmployeeNames.length === 0
      ? statusFiltered
      : statusFiltered.filter((r) =>
          selectedEmployeeNames.includes(r.employeeName),
        );
  }, [filteredLeaveRequests, selectedEmployeeNames, employeeStatusFilter]);
  // Plan entries carry no status, so they follow the status-filtered names.
  const visibleAnnualPlan = useMemo(() => {
    const statusFiltered = annualPlan.filter((p) =>
      statusEmployeeNames.has(p.employeeName || ""),
    );
    return selectedEmployeeNames.length === 0
      ? statusFiltered
      : statusFiltered.filter((p) =>
          selectedEmployeeNames.includes(p.employeeName || ""),
        );
  }, [annualPlan, selectedEmployeeNames, statusEmployeeNames]);

  const groupByRequestStatus = useMemo(() => {
    return allLeaveReq.reduce((acc: Record<string, LeaveRequest[]>, request: LeaveRequest) => {
      const status = request.requestStatus;
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(request);
      return acc;
    }, {} as Record<string, LeaveRequest[]>);
  }, [allLeaveReq]);

  const statisticsItems = [
    { key: "year", label: "Year", value: annualLeaveYear.value },
    { key: "employees", label: "Employees", value: statusUsers.length },
    {
      key: "pendingApprovals",
      label: "Pending Approval",
      value: groupByRequestStatus.AWAITING_MANAGER?.length || 0,
      onClick: () => {
        setStatisticModalType("Pending");
        setIsStatisticsModalOpen(true);
      },
    },
    {
      key: "cancelledPendingApprovals",
      label: "Cancel Pending Approval",
      value: groupByRequestStatus.AWAITING_MANAGER_CANCELLATION?.length || 0,
      onClick: () => {
        setStatisticModalType("PendingCancelled");
        setIsStatisticsModalOpen(true);
      },
    },
  ];

  const toggleLeaveType = useCallback((type: string) => {
    if (type === "BANK_HOLIDAY") {
      setBankHolidayVisible((prev) => !prev);
      return;
    }
    setActiveLeaveTypeFilters((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  }, []);

  useEffect(() => {
    if (!authUser) return;
    fetchUsers();
    fetchHolidays();
    fetchTeamRequests();
    fetchLegendColors();
    fetchAnnualPlan();
  }, [
    authUser,
    fetchAnnualPlan,
    fetchHolidays,
    fetchLegendColors,
    fetchTeamRequests,
    fetchUsers,
  ]);

  const handleYearChange = useCallback((value: string) => {
    const found = years.find((y) => y.value === value);
    if (found) setAnnualLeaveYear(found);
  }, []);

  const screens = Grid.useBreakpoint() || {};
  const isMobile = !screens.md;

  return (
    <div className="leave-calendar-wrapper">
      <Typography.Title level={2} className="team-calendar-title">
        Team Calendar
      </Typography.Title>
      <Row>
        <Row
          className="al-years-header team-calendar-header"
          align="middle"
          wrap={false}
        >
          <Col xs={24} lg="auto">
            <div className="calendar-filter-group">
              <div className="calendar-filter-field">
                <span className="header-label">Year</span>
                <Select
                  className="al-year-select"
                  options={years.map((y) => ({
                    label: y.text,
                    value: y.value,
                  }))}
                  value={annualLeaveYear.value}
                  onChange={handleYearChange}
                />
              </div>
              <div className="calendar-filter-field">
                <span className="header-label">Month</span>
                <Select
                  className="al-year-select"
                  options={monthOptions}
                  value={focusedMonth}
                  onChange={(value) => setFocusedMonth(value)}
                />
              </div>
              <div className="calendar-filter-field">
                <span className="header-label">Active</span>
                <Select
                  className="al-year-select"
                  options={EMPLOYEE_STATUS_OPTIONS}
                  value={employeeStatusFilter}
                  onChange={setEmployeeStatusFilter}
                />
              </div>
              <div className="calendar-filter-field">
                <span className="header-label">Employees</span>
                <Select
                  mode="multiple"
                  allowClear
                  className="al-year-select"
                  placeholder="All employees"
                  options={employeeOptions}
                  value={selectedEmployeeNames}
                  onChange={setSelectedEmployeeNames}
                  maxTagCount="responsive"
                />
              </div>
            </div>
          </Col>
          <Col xs={24} flex="auto" className="stats-container team-stats-container">
            {loading ? (
              <div className="stats-loading">
                <Spin size="small" />
                <span>Loading statistics...</span>
              </div>
            ) : (
              <StatisticsWidget statisticsItems={statisticsItems} />
            )}
          </Col>
        </Row>

        {!isDataLoaded ? (
          <>
            <Spin size="small" />
            <p>Loading table...</p>
          </>
        ) : isMobile ? (
          <AnnualLeaveTableMobile
            annualLeaveYear={annualLeaveYear.value}
            leaveRequests={visibleLeaveRequests}
            users={visibleUsers}
            sortOrder="asc"
            selectedLeaveTypes={selectedLeaveTypes}
            bankHolidays={bankHolidays}
            legendColors={legendColors}
            annualPlan={visibleAnnualPlan}
          />
        ) : (
          <div className="calendar-scroll-wrapper">
            <AnnualLeaveTable
              annualLeaveYear={annualLeaveYear.value}
              leaveRequests={visibleLeaveRequests}
              users={visibleUsers}
              sortOrder="asc"
              focusedMonth={focusedMonth}
              // onDateClick={handleOpenLeaveModal}
              selectedLeaveTypes={selectedLeaveTypes}
              bankHolidays={bankHolidays}
              legendColors={legendColors}
              annualPlan={visibleAnnualPlan}
            />
          </div>
        )}
      </Row>

      <div className="leave-legend">
        {ALL_LEGEND_TYPES.map((type) => {
          const formatted = type
            .toLowerCase()
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase());

          return (
            <span
              key={type}
              className={`legend-item ${
                selectedLeaveTypes.includes(type) ? "active" : "inactive"
              }`}
              style={
                legendColors[type]
                  ? { backgroundColor: legendColors[type] }
                  : undefined
              }
              onClick={() => toggleLeaveType(type)}
            >
              {formatted}
            </span>
          );
        })}
      </div>

      {(isAdmin || isHR) && (
        <Row className="mt-5 w-full">
          <StockRecord />
        </Row>
      )}
      <Row className="mt-5 w-full">
        <TeamLeaveRequests />
      </Row>

      <RequestsTeamModal
        isOpen={isStatisticsModalOpen}
        setIsOpen={setIsStatisticsModalOpen}
        type={statisticModalType}
      />
    </div>
  );
};

export default TeamCalendar;
